import CryptoJS from "crypto-js";
import request from "@/modules/huyu-framework/backend/utils/request";

// 本地记住密码加密用的固定盐（源码中合理；安全性依赖浏览器指纹，密钥不会出现在传输流程）
const LOCAL_KEY_SALT = "huyu-local-encrypt-salt-v1";

/**
 * 获取登录动态加密密钥（服务器每次生成，Redis 5分钟单次有效）
 * @returns {Promise<{keyId: string, key: string}>}
 */
export function getEncryptKey() {
  return request({
    url: "/system/encrypt/getEncryptKey",
    headers: { isToken: false, silentError: true },
    method: "get",
  });
}

/**
 * 使用服务器动态密钥加密密码（AES-256-CBC/PKCS7，密钥为 32 字节 base64）
 * 返回格式：base64(iv):base64(密文)，与后端 decryptWithDynamicKey 对应
 * @param {string} password 明文密码
 * @param {string} keyBase64 服务器下发的 32 字节密钥 base64
 * @returns {string}
 */
export function encryptPassword(password, keyBase64) {
  const iv = CryptoJS.lib.WordArray.random(16);
  const key = CryptoJS.enc.Base64.parse(keyBase64);
  const encrypted = CryptoJS.AES.encrypt(password, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
  const cipherBase64 = encrypted.ciphertext.toString(CryptoJS.enc.Base64);
  return ivBase64 + ":" + cipherBase64;
}

/**
 * 浏览器指纹：只取设备与浏览器的稳定信息（userAgent、平台、语言），不含分辨率等易变值
 * 使用 SHA-256 派生为固定长度指纹串
 * @returns {string}
 */
export function getBrowserFingerprint() {
  const parts = [navigator.userAgent, navigator.platform, navigator.language];
  return CryptoJS.SHA256(parts.join("|")).toString();
}

// 派生本地加解密密钥：固定盐 + 浏览器指纹
function getLocalKey() {
  return CryptoJS.SHA256(LOCAL_KEY_SALT + getBrowserFingerprint());
}

/**
 * 本地记住密码加密：固定盐+浏览器指纹派生密钥（AES-256-CBC）
 * @param {string} password 明文密码
 * @returns {string}
 */
export function encryptLocalPassword(password) {
  const iv = CryptoJS.lib.WordArray.random(16);
  const key = getLocalKey();
  const encrypted = CryptoJS.AES.encrypt(password, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
  const cipherBase64 = encrypted.ciphertext.toString(CryptoJS.enc.Base64);
  return ivBase64 + ":" + cipherBase64;
}

/**
 * 本地记住密码解密（固定盐+浏览器指纹派生密钥）
 * @param {string} cipher 加密串
 * @returns {string|null} 解密失败返回 null
 */
export function decryptLocalPassword(cipher) {
  try {
    const parts = cipher.split(":");
    if (parts.length !== 2) return null;
    const iv = CryptoJS.enc.Base64.parse(parts[0]);
    const key = getLocalKey();
    const decrypted = CryptoJS.AES.decrypt(parts[1], key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const plain = decrypted.toString(CryptoJS.enc.Utf8);
    return plain ? plain : null;
  } catch (e) {
    return null;
  }
}
